/**
 * Taking a photo with the computer's camera.
 *
 * The camera button used `capture="environment"` on a file input. That
 * works on a phone and does nothing of the sort on a desktop browser,
 * where the attribute is ignored and you get an ordinary file picker —
 * reported as "the camera icon is not opening the camera".
 *
 * So on a desktop this opens the camera properly: a live preview, a
 * shutter, and a review step before anything is sent. On a phone the
 * file input is still the better path, because it hands you the real
 * camera app with its flash, focus and lenses.
 */

import { useEffect, useRef, useState } from 'react'
import './CameraSheet.css'

export default function CameraSheet ({ onCapture, onClose }) {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [error, setError] = useState(null)
  const [shot, setShot] = useState(null)   // { blob, url }
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false
        })
        if (!alive) {
          for (const t of stream.getTracks()) t.stop()
          return
        }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
      } catch {
        if (alive) setError('ZIvapp needs permission to use your camera.')
      }
    })()

    return () => {
      alive = false
      // Released on the way out, or the camera light stays on and the
      // tab keeps claiming the device.
      for (const t of streamRef.current?.getTracks() ?? []) t.stop()
      streamRef.current = null
    }
  }, [])

  function take () {
    const video = videoRef.current
    // videoWidth is 0 until the first frame, and a zero-sized canvas
    // yields no blob — the shutter appeared to do nothing.
    if (!video || !video.videoWidth) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0)

    canvas.toBlob(blob => {
      if (blob) setShot({ blob, url: URL.createObjectURL(blob) })
    }, 'image/jpeg', 0.9)
  }

  function send () {
    if (!shot) return
    onCapture(new File([shot.blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }))
    onClose()
  }

  function retake () {
    URL.revokeObjectURL(shot.url)
    setShot(null)
  }

  return (
    <div className="cam" role="dialog" aria-modal="true" aria-label="Camera">
      <header className="cam__bar">
        <button type="button" className="cam__icon" onClick={onClose} aria-label="Close camera">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" />
          </svg>
        </button>
        <span className="cam__title">{shot ? 'Send this photo?' : 'Camera'}</span>
      </header>

      <div className="cam__stage">
        {error ? (
          <p className="cam__error">{error}</p>
        ) : shot ? (
          <img className="cam__shot" src={shot.url} alt="" />
        ) : (
          // Mirrored, because an unmirrored selfie preview is disorienting
          // — you move left and the image moves right.
          <video
            ref={videoRef}
            className="cam__video"
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => setReady(true)}
          />
        )}
      </div>

      <footer className="cam__foot">
        {shot ? (
          <>
            <button type="button" className="cam__text-btn" onClick={retake}>
              Retake
            </button>
            <button
              type="button"
              className="cam__send"
              onClick={send}
              data-testid="camera-send"
            >
              Send
            </button>
          </>
        ) : (
          !error && (
            <button
              type="button"
              className="cam__shutter"
              onClick={take}
              disabled={!ready}
              aria-label="Take a photo"
              data-testid="camera-shutter"
            />
          )
        )}
      </footer>
    </div>
  )
}
